import React, { FC, RefObject, useEffect, useRef, useState } from 'react'
import {
	CheckboxList,
	RadioList,
	RequestFormPDFProps,
	RequestInputs
} from '@utils/request'
import requestForm from '@components/requestForms/request.form'
import { Collapse } from 'react-collapse'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import TextArea from './inputs/textarea.inputs'
import CheckboxInput from './inputs/checkbox.input'
import { joinClasses, makeClasses } from '@utils/styles'
import * as pageUtils from './__forms.utils'
import { useAppSelector } from '@services/store'
import Notification from '@components/ui/notification'
import Text from '@components/ui/text'
import { formatStrapiText } from '@utils/methods'
import ConcernedInfosSection from '@components/ui/concernedInfosSection'
import { ConcernFieldNames } from '@components/ui/concernedInfosSection/__index.utils'
import { Colors } from '@utils/css-variables'
import { AddressCoordinates } from '@services/models'
import { getAddressByCoordinates } from '@components/ui/addressForm/__index.utils'

type Classes = {
	adresseBlock: string
	checkbox2: string
	checkbox2Label: string
	checkbox2Container: string
	checkboxWrapper: string
}

const classes: Classes = makeClasses({
	adresseBlock: {
		border: `1px solid ${Colors.greyAlto}`,
		padding: '20px 25px',
		marginBottom: '10px'
	},
	checkbox2: {
		width: 'fit-content',
		position: 'absolute',
		verticalAlign: 'baseline',
		clip: 'rect(1px, 1px, 1px, 1px)'
	},
	checkbox2Label: {
		fontSize: '16px',
		padding: '0',
		color: Colors.lightBlack,
		position: 'relative',
		paddingLeft: '27px',
		fontWeight: 'normal',
		cursor: 'pointer',
		lineHeight: '17px',
		':before': {
			content: "''",
			position: 'absolute',
			top: '0',
			left: '0',
			width: '16px',
			height: '16px',
			border: `1px solid ${Colors.lightGrey3}`,
			background: Colors.white,
			outline: 'none !important'
		},
		':after': {
			content: "''",
			position: 'absolute',
			top: '4px',
			left: '4px',
			width: '10px',
			height: '10px',
			background: Colors.secondary,
			outline: 'none',
			opacity: '0',
			transition: 'all .15s ease-out'
		},

		'&.error-check': {
			color: `${Colors.red} !important`,
			':before': {
				border: `1px solid ${Colors.red}`
			}
		}
	},
	checkbox2Container: {
		'& input[value=true] ~ label:after': {
			opacity: '.99'
		}
	},
	checkboxWrapper: {
		display: 'block',
		margin: '0 0 5px 0',
		background: Colors.ligthGrey2,
		border: `1px solid ${Colors.greyAlto}`,
		padding: '14px 12px'
	}
})

const CollectBulkyItemsForm: FC<RequestFormPDFProps> = ({
	inputs,
	errors,
	subSteps,
	isSummaryStep,
	steps,
	toPrint,
	onSetSubSteps,
	onFixError,
	onSetInputs,
	setCurrentStep,
	setDisableNextButton
}) => {
	const {
		authUser,
		pageAssets,
		UpdateRequestInputsArray,
		UpdateRequestInputsErrorsArray
	} = requestForm()

	const answers = useAppSelector((state) => state.request.configs)

	const [showDeclarationAdresseBlock, setShowDeclarationAdresseBlock] =
		useState<boolean>(answers.sameAddressAsApplicant == true ? false : true)

	const [isSameDeclarationAdresseInput, setIsSameDeclarationAdresseInput] =
		useState<RequestInputs>({
			name: 'sameAddressAsApplicant',
			label: pageAssets?.request_form_isSameAdresse_label,
			labelKey: 'request_form_isSameAdresse_label',
			value: answers.sameAddressAsApplicant?.toString() || 'false',
			required: true,
			ref: useRef<HTMLSelectElement>(null)
		})

	const [buildingBatchNumberInput, setBuildingBatchNumberInput] =
		useState<RequestInputs>({
			name: 'buildingBatchNumber',
			label: '',
			labelKey: '',
			value: answers.buildingBatchNumber || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null)
		})

	const [buildingRegistrationNumberInput, setBuildingRegistrationNumberInput] =
		useState<RequestInputs>({
			name: 'buildingRegistrationNumber',
			label: '',
			labelKey: '',
			value: answers.buildingRegistrationNumber || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null)
		})

	// Main checkbox input
	const [bulkyItemsCheckbox, setBulkyItemsCheckbox] = useState<RequestInputs>({
		name: 'bulkyItemsCheckbox',
		label: pageAssets?.request_form_collectBulkyItems_checkbox_label,
		labelKey: 'request_form_collectBulkyItems_checkbox_label',
		value: answers.bulkyItemsCheckbox || '',
		valueKey: '',
		required: true,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
	})

	// Four text inputs displayed under checkbox
	const [firstItem, setFirstItem] = useState<RequestInputs>({
		name: 'bulkyItemsFirst',
		label:
			pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
		labelKey:
			'request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision',
		value: answers.bulkyItemsFirst || '',
		required:
			answers.bulkyItemsCheckbox?.includes(
				REQUEST_CONFIGS_KEYS.bulkyItemType.matress
			) || false,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
	})

	const [secondItem, setSecondItem] = useState<RequestInputs>({
		name: 'bulkyItemsSecond',
		label:
			pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
		labelKey:
			'request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision',
		value: answers.bulkyItemsSecond || '',
		required:
			answers.bulkyItemsCheckbox?.includes(
				REQUEST_CONFIGS_KEYS.bulkyItemType.dryMaterials
			) || false,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
	})

	const [thirdItem, setThirdItem] = useState<RequestInputs>({
		name: 'bulkyItemsThird',
		label:
			pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
		labelKey:
			'request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision',
		value: answers.bulkyItemsThird || '',
		required:
			answers.bulkyItemsCheckbox?.includes(
				REQUEST_CONFIGS_KEYS.bulkyItemType.christmasTreeBranches
			) || false,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
	})

	const [fourthItem, setFourthItem] = useState<RequestInputs>({
		name: 'bulkyItemsFourth',
		label:
			pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
		labelKey:
			'request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision',
		value: answers.bulkyItemsFourth || '',
		required:
			answers.bulkyItemsCheckbox?.includes(
				REQUEST_CONFIGS_KEYS.bulkyItemType.residues
			) || false,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
	})

	const getCadastralAndMatriculeNumber = (coordinates: AddressCoordinates) => {
		getAddressByCoordinates(coordinates).then((res) => {
			setBuildingBatchNumberInput({
				...buildingBatchNumberInput,
				value: res?.data?.features?.[0].properties.cadastre
			})

			setBuildingRegistrationNumberInput({
				...buildingRegistrationNumberInput,
				value: res?.data?.features?.[0].properties.matricule
			})
		})
	}

	const onSelectIsSameDeclarationAdresseInput = (value) => {
		setIsSameDeclarationAdresseInput({
			...isSameDeclarationAdresseInput,
			value: `${value}`
		})

		if (value == true) {
			getCadastralAndMatriculeNumber(authUser?.profile?.address.coordinates!)
		}

		setTimeout(() => {
			setShowDeclarationAdresseBlock(!value)
		}, 100)

		onFixError(
			UpdateRequestInputsErrorsArray(errors, isSameDeclarationAdresseInput.name)
		)
	}

	const onSelectCheckbox = (value: string) => {
		let newValue
		let required = true
		if (bulkyItemsCheckbox.value?.includes(value)) {
			newValue = pageUtils.removeStrFromStrArray(
				value,
				bulkyItemsCheckbox.value
			)
			required = false
		} else {
			newValue =
				bulkyItemsCheckbox.value == ''
					? value
					: bulkyItemsCheckbox.value + `, ${value}`
		}

		setBulkyItemsCheckbox({
			...bulkyItemsCheckbox,
			value: newValue
		})

		switch (value) {
			case REQUEST_CONFIGS_KEYS.bulkyItemType.matress:
				setFirstItem({
					...firstItem,
					value: bulkyItemsCheckbox?.value.includes(
						REQUEST_CONFIGS_KEYS.bulkyItemType.matress
					)
						? firstItem.value
						: '',
					required: required
				})
				break
			case REQUEST_CONFIGS_KEYS.bulkyItemType.dryMaterials:
				setSecondItem({
					...secondItem,
					value: bulkyItemsCheckbox?.value.includes(
						REQUEST_CONFIGS_KEYS.bulkyItemType.dryMaterials
					)
						? secondItem.value
						: '',
					required: required
				})
				break
			case REQUEST_CONFIGS_KEYS.bulkyItemType.christmasTreeBranches:
				setThirdItem({
					...thirdItem,
					value: bulkyItemsCheckbox?.value.includes(
						REQUEST_CONFIGS_KEYS.bulkyItemType.christmasTreeBranches
					)
						? thirdItem.value
						: '',
					required: required
				})
				break
			case REQUEST_CONFIGS_KEYS.bulkyItemType.residues:
				setFourthItem({
					...fourthItem,
					value: bulkyItemsCheckbox?.value.includes(
						REQUEST_CONFIGS_KEYS.bulkyItemType.residues
					)
						? fourthItem.value
						: '',
					required: required
				})
				break
		}

		onFixError(
			errors.filter(
				(error) =>
					![
						bulkyItemsCheckbox.name,
						firstItem.name,
						secondItem.name,
						thirdItem.name,
						fourthItem.name
					].includes(error)
			)
		)
	}

	const onSelectFirstItem = (value: string) => {
		setFirstItem({ ...firstItem, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, firstItem.name))
	}

	const onSelectSecondItem = (value: string) => {
		setSecondItem({ ...secondItem, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, secondItem.name))
	}

	const onSelectThirdItem = (value: string) => {
		setThirdItem({ ...thirdItem, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, thirdItem.name))
	}

	const onSelectFourthItem = (value: string) => {
		setFourthItem({ ...fourthItem, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, fourthItem.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, isSameDeclarationAdresseInput))
	}, [isSameDeclarationAdresseInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, buildingBatchNumberInput))
	}, [buildingBatchNumberInput])

	useEffect(() => {
		onSetInputs(
			UpdateRequestInputsArray(inputs, buildingRegistrationNumberInput)
		)
	}, [buildingRegistrationNumberInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, isSameDeclarationAdresseInput))
	}, [isSameDeclarationAdresseInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, bulkyItemsCheckbox))
	}, [bulkyItemsCheckbox])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, firstItem))
	}, [firstItem])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, secondItem))
	}, [secondItem])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, thirdItem))
	}, [thirdItem])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, fourthItem))
	}, [fourthItem])

	useEffect(() => {
		onSetSubSteps({
			map: false,
			details_1: true,
			details_2: true,
			requiredDocument: false,
			position:
				subSteps?.position && String(subSteps?.position) !== ''
					? subSteps?.position
					: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			steps: [
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
			]
		})
	}, [])

	const backToForm = (step: number, subStep: string) => {
		if (setCurrentStep && onSetSubSteps && subSteps) {
			setCurrentStep(step)
			onSetSubSteps({
				...subSteps,
				position: subStep as REQUEST_TN_DETAILS_STEPS_NAME
			})
		}
	}

	const diplayOptionDetails = (input, select) => {
		return (
			<TextArea
				id={input.name}
				label={input.label}
				value={input.value}
				onChange={select}
				hasError={errors.includes(input.name)}
				required={input.required}
				ref={input.ref as RefObject<HTMLTextAreaElement>}
				maxCharacters={500}
			/>
		)
	}

	// Constants for each item's TextArea details
	const firstItemDetails = diplayOptionDetails(firstItem, onSelectFirstItem)
	const secondItemDetails = diplayOptionDetails(secondItem, onSelectSecondItem)
	const thirdItemDetails = diplayOptionDetails(thirdItem, onSelectThirdItem)
	const fourthItemDetails = diplayOptionDetails(fourthItem, onSelectFourthItem)

	const bulkyItemsCheckboxList: CheckboxList[] = [
		{
			label:
				pageAssets?.request_form_collectBulkyItems_christmasTreeBranches_label,
			extraText:
				pageAssets?.request_form_collectBulkyItems_christmasTreeBranches_description,
			key: 'request_form_commerialPermit_typesOfWorkEnseigne_artisticWallRepresentation',
			value: REQUEST_CONFIGS_KEYS.bulkyItemType.christmasTreeBranches,
			checked: false,
			index: 0
		},
		{
			label: pageAssets?.request_form_collectBulkyItems_matress_label,
			extraText: pageAssets?.request_form_collectBulkyItems_matress_description,
			key: 'request_form_commerialPermit_typesOfWorkEnseigne_newSign',
			value: REQUEST_CONFIGS_KEYS.bulkyItemType.matress,
			checked: false,
			index: 1
		},
		{
			label: pageAssets?.request_form_collectBulkyItems_dryMaterials_label,
			extraText:
				pageAssets?.request_form_collectBulkyItems_dryMaterials_description,
			key: 'request_form_commerialPermit_typesOfWorkEnseigne_replaceSign',
			value: REQUEST_CONFIGS_KEYS.bulkyItemType.dryMaterials,
			checked: false,
			index: 2
		},
		{
			label: pageAssets?.request_form_collectBulkyItems_residues_label,
			extraText:
				pageAssets?.request_form_collectBulkyItems_residues_description,
			key: 'request_form_commerialPermit_typesOfWorkEnseigne_artisticWallRepresentation',
			value: REQUEST_CONFIGS_KEYS.bulkyItemType.residues,
			checked: false,
			index: 3
		}
	]

	const radioListYesNo: RadioList[] = [
		{
			label: pageAssets?.request_form_select_yes,
			value: 'true',
			key: 'request_form_select_yes'
		},
		{
			label: pageAssets?.request_form_select_no,
			value: 'false',
			key: 'request_form_select_no'
		}
	]

	const getItemFromString = (list, answer?: string): any[] => {
		return list
			.filter((element) => answer?.includes(element.value))
			.map((item) => item)
	}

	const getLabelFromList = (list, value) => {
		return list?.find((element) => element.value == value?.toString())?.label
	}

	if (isSummaryStep) {
		return (
			<section
				className={pageUtils.classes.noMargin}
				style={{ padding: '0px' }}
			>
				{subSteps?.details_1 && (
					<>
						<div>
							<a
								style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
								className={joinClasses([
									pageUtils.classes.btn,
									pageUtils.classes.button,
									pageUtils.classes.buttonOutline
								])}
								onClick={() =>
									backToForm(
										steps?.form!,
										REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
									)
								}
							>
								{pageAssets?.label_modify}
							</a>
							<h4 className={pageUtils.classes.h4}>
								{pageAssets?.request_form_parkingPermit_address_of_request}
							</h4>
						</div>

						<div>
							<strong>{pageAssets?.request_form_isSameAdresse_label}</strong>
							<br />
							<span className={pageUtils.classes.answer}>
								{getLabelFromList(
									radioListYesNo,
									answers?.sameAddressAsApplicant
								)}
							</span>
						</div>

						{answers?.sameAddressAsApplicant?.toString() == 'false' && (
							<div>
								<strong>{pageAssets?.label_address}</strong>
								<br />
								<span className={pageUtils.classes.answer}>
									{answers?.concernedAdresse}
								</span>
							</div>
						)}
						{(answers?.concernedAppartement ||
							(authUser?.profile?.address.apartment &&
								answers?.sameAddressAsApplicant?.toString() ==
									radioListYesNo[0].value)) && (
							<div>
								<strong>{`${formatStrapiText(
									pageAssets?.label_apartment_office
								)}`}</strong>
								<br />
								<span className={pageUtils.classes.answer}>
									{!answers?.sameAddressAsApplicant
										? answers?.concernedAppartement
										: authUser?.profile?.address.apartment}
								</span>
							</div>
						)}
					</>
				)}
				{subSteps?.details_2 && (
					<>
						<div>
							<a
								style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
								className={joinClasses([
									pageUtils.classes.btn,
									pageUtils.classes.button,
									pageUtils.classes.buttonOutline
								])}
								onClick={() =>
									backToForm(
										steps?.form!,
										REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
									)
								}
							>
								{pageAssets?.label_modify}
							</a>
							<h4 className={pageUtils.classes.h4}>
								{pageAssets?.request_form_collectBulkyItems_section_title}
							</h4>
						</div>

						<div style={{ marginTop: '20px' }}>
							<strong>{pageAssets[bulkyItemsCheckbox.labelKey]}</strong>
							<br />
							<span className={pageUtils.classes.answer}>
								{getItemFromString(
									bulkyItemsCheckboxList,
									answers?.bulkyItemsCheckbox
								).map((item, index) => {
									return (
										<div key={`${index}-bulkyItems-answer`}>
											<strong key={`${index}-bulkyItems-summary`}>
												{item.label}
											</strong>
											<br />
											<span>
												{(() => {
													switch (item.value) {
														case REQUEST_CONFIGS_KEYS.bulkyItemType.matress:
															return answers.bulkyItemsFirst
														case REQUEST_CONFIGS_KEYS.bulkyItemType
															.dryMaterials:
															return answers.bulkyItemsSecond
														case REQUEST_CONFIGS_KEYS.bulkyItemType
															.christmasTreeBranches:
															return answers.bulkyItemsThird
														case REQUEST_CONFIGS_KEYS.bulkyItemType.residues:
															return answers.bulkyItemsFourth
														default:
															return ''
													}
												})()}
											</span>
										</div>
									)
								})}
							</span>
						</div>
					</>
				)}
			</section>
		)
	}

	return (
		<>
			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1 && (
				<Collapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
					}
				>
					<section>
						<h4 className={pageUtils.classes.h4}>
							{pageAssets?.request_form_parkingPermit_address_of_request}
						</h4>
						{/*authUser?.profile?.address.inMunicipality && (
							<div className={classes.checkbox2Container}>
								<span className={classes.checkboxWrapper}>
									<input
										onClick={(e: any) =>
											onSelectIsSameDeclarationAdresseInput(e.target.checked)
										}
										value={isSameDeclarationAdresseInput.value}
										className={classes.checkbox2}
										type="checkbox"
										name={isSameDeclarationAdresseInput.name}
										id={isSameDeclarationAdresseInput.labelKey}
									/>
									<label
										className={classes.checkbox2Label}
										htmlFor={isSameDeclarationAdresseInput.labelKey}
									>
										{pageAssets?.request_form_isSameAdresse_label}
									</label>
								</span>
							</div>
						)*/}

						<div style={{ marginBottom: '20px' }}>
							{showDeclarationAdresseBlock == true ||
							!authUser?.profile?.address.inMunicipality ? (
								<ConcernedInfosSection
									errors={errors}
									inputs={inputs}
									onFixError={onFixError}
									onSetInputs={onSetInputs}
									isConcernedInfosRequired={
										`${isSameDeclarationAdresseInput.value}` == 'false'
									}
									requiredFields={[
										ConcernFieldNames.concernedAdresse,
										ConcernFieldNames.concernedAppartement,
										ConcernFieldNames.concernedCoordinates
									]}
									onGetCadastralAndMatriculeNumber={
										getCadastralAndMatriculeNumber
									}
								/>
							) : (
								<div className={classes.adresseBlock}>
									<strong>{pageAssets?.label_address}</strong>
									<br />
									{authUser?.profile?.address.address}
									<br />
									{authUser?.profile?.address.city}
									{authUser?.profile?.address.state
										? ` (${authUser?.profile?.address.state}) `
										: ' '}
									{authUser?.profile?.address.postalCode}
									<br />
									{authUser?.profile?.address.apartment
										? `${formatStrapiText(
												pageAssets?.label_apartment_office
										  )} ${authUser?.profile?.address.apartment}`
										: ''}
								</div>
							)}
						</div>
					</section>
				</Collapse>
			)}
			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2 && (
				<Collapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
					}
				>
					<section>
						<h4 className={pageUtils.classes.h4}>
							{pageAssets?.request_form_collectBulkyItems_section_title}
						</h4>

						<Notification
							type="info"
							text={
								pageAssets?.request_form_collectBulkyItems_measurement_notification
							}
						/>

						<Notification
							type="info"
							text={
								pageAssets?.request_form_collectBulkyItems_schedule_notification
							}
						/>

						<div className={pageUtils.classes.radioLabel}>
							<CheckboxInput
								name={bulkyItemsCheckbox.name}
								label={bulkyItemsCheckbox.label}
								description={bulkyItemsCheckbox.description}
								value={`${bulkyItemsCheckbox.value}`}
								list={bulkyItemsCheckboxList}
								onChange={onSelectCheckbox}
								required={bulkyItemsCheckbox.required}
								hasError={errors.includes(bulkyItemsCheckbox.name)}
								ref={bulkyItemsCheckbox.ref as RefObject<HTMLInputElement>}
								errors={errors}
								shouldNotDivide
								hasBackgroundWrapper
								onFixError={onFixError}
								newCheckValue
								subTextInputs={[
									thirdItemDetails,
									firstItemDetails,
									secondItemDetails,
									fourthItemDetails
								]}
							/>
						</div>

						<Text
							content={
								pageAssets?.request_form_collectBulkyItems_item_info_list
							}
						/>
					</section>
				</Collapse>
			)}
		</>
	)
}

export default CollectBulkyItemsForm
